import { useEffect, useState } from 'react';
import { defaultDisplayPreferences, loadDisplayPreferencesForUser } from '../preferences/displayPreferencesStorage';
import type { LocalAuthUser } from '../data/types';
import { updateUserPreferences } from '../data/repository';

export interface DisplayPreferencesState {
  darkMode: boolean;
  lightTableColor: string;
  darkTableColor: string;
  lightWallColor: string;
  darkWallColor: string;
}

export interface DisplayPreferencesActions extends DisplayPreferencesState {
  currentTableColor: string;
  currentWallColor: string;
  setDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
  setLightTableColor: React.Dispatch<React.SetStateAction<string>>;
  setDarkTableColor: React.Dispatch<React.SetStateAction<string>>;
  setLightWallColor: React.Dispatch<React.SetStateAction<string>>;
  setDarkWallColor: React.Dispatch<React.SetStateAction<string>>;
  toggleDarkMode: () => void;
  updateTableColor: (value: string) => void;
  updateWallColor: (value: string) => void;
}

export function useDisplayPreferences(currentUser?: LocalAuthUser | null): DisplayPreferencesActions {
  const [lightTableColor, setLightTableColor] = useState<string>(defaultDisplayPreferences.lightTableColor);
  const [darkTableColor, setDarkTableColor] = useState<string>(defaultDisplayPreferences.darkTableColor);
  const [lightWallColor, setLightWallColor] = useState<string>(defaultDisplayPreferences.lightWallColor);
  const [darkWallColor, setDarkWallColor] = useState<string>(defaultDisplayPreferences.darkWallColor);
  const [darkMode, setDarkMode] = useState<boolean>(defaultDisplayPreferences.darkMode);

  useEffect(() => {
    let cancelled = false;
    void loadDisplayPreferencesForUser(currentUser?.preferences).then((preferences) => {
      if (cancelled) {
        return;
      }
      setDarkMode(preferences.darkMode);
      setLightTableColor(preferences.lightTableColor);
      setDarkTableColor(preferences.darkTableColor);
      setLightWallColor(preferences.lightWallColor);
      setDarkWallColor(preferences.darkWallColor);
    });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.preferences]);

  const currentTableColor = darkMode ? darkTableColor : lightTableColor;
  const currentWallColor = darkMode ? darkWallColor : lightWallColor;

  function toggleDarkMode(): void {
    setDarkMode((prev) => {
      const next = !prev;
      if (currentUser) {
        void updateUserPreferences({ darkMode: next }).catch(() => undefined);
      }
      return next;
    });
  }

  function updateTableColor(value: string): void {
    if (darkMode) {
      setDarkTableColor(value);
      if (currentUser) {
        void updateUserPreferences({ darkTableColor: value }).catch(() => undefined);
      }
      return;
    }

    setLightTableColor(value);
    if (currentUser) {
      void updateUserPreferences({ lightTableColor: value }).catch(() => undefined);
    }
  }

  function updateWallColor(value: string): void {
    if (darkMode) {
      setDarkWallColor(value);
      if (currentUser) {
        void updateUserPreferences({ darkWallColor: value }).catch(() => undefined);
      }
      return;
    }

    setLightWallColor(value);
    if (currentUser) {
      void updateUserPreferences({ lightWallColor: value }).catch(() => undefined);
    }
  }

  return {
    darkMode,
    lightTableColor,
    darkTableColor,
    lightWallColor,
    darkWallColor,
    currentTableColor,
    currentWallColor,
    setDarkMode,
    setLightTableColor,
    setDarkTableColor,
    setLightWallColor,
    setDarkWallColor,
    toggleDarkMode,
    updateTableColor,
    updateWallColor,
  };
}
